import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { Image, Type, Square, List, LayoutPanelTop, ChevronRight, ChevronDown } from 'lucide-react';
import { ComponentType, CanvasComponent } from '@/types';
import { useState } from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { cn } from '@/lib/utils';

const DRAGGABLE_COMPONENTS = [
  { type: ComponentType.BUTTON, icon: Square, label: 'Button' },
  { type: ComponentType.INPUT, icon: Type, label: 'Input' },
  { type: ComponentType.TEXT, icon: Type, label: 'Text' },
  { type: ComponentType.IMAGE, icon: Image, label: 'Image' },
  { type: ComponentType.CARD, icon: Square, label: 'Card' },
  { type: ComponentType.LIST, icon: List, label: 'List' },
  { type: ComponentType.PANEL, icon: LayoutPanelTop, label: 'Container Panel' },
];

function DraggableComponent({ type, icon: Icon, label }: {
  type: ComponentType;
  icon: any;
  label: string;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `draggable-${type}`,
    data: { type, isTemplate: true },
  });

  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    zIndex: isDragging ? 1000 : undefined,
  } : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className="cursor-move touch-none"
    >
      <Button
        variant="outline"
        className="w-full justify-start"
      >
        <Icon className="w-4 h-4 mr-2" />
        {label}
      </Button>
    </div>
  );
}

interface HierarchyItemProps {
  component: CanvasComponent;
  components: CanvasComponent[];
  onSelect: (component: CanvasComponent) => void;
  selectedId: string | null;
  level?: number;
  onDrop?: (draggedId: string, droppedOnId: string) => void;
}

function HierarchyItem({ 
  component, 
  components, 
  onSelect, 
  selectedId, 
  level = 0,
  onDrop,
}: HierarchyItemProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const childComponents = components.filter(c => c.properties.parentId === component.id);
  
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `droppable-${component.id}`,
    data: { id: component.id, isHierarchyItem: true },
  });

  const { attributes, listeners, setNodeRef: setDragRef, transform, isDragging } = useDraggable({
    id: `draggable-hierarchy-${component.id}`,
    data: { id: component.id, isHierarchyItem: true },
  });

  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    zIndex: isDragging ? 1000 : undefined,
  } : undefined;

  const dragRef = (element: HTMLElement | null) => {
    setDragRef(element);
    setDropRef(element);
  };

  return (
    <div style={style}>
      <div
        ref={dragRef}
        {...attributes}
        {...listeners}
        className={cn(
          "flex items-center py-1 px-2 rounded cursor-pointer",
          selectedId === component.id && "bg-accent",
          isOver && "bg-primary/20",
          isDragging && "opacity-50"
        )}
        style={{ paddingLeft: `${level * 12}px` }}
        onClick={() => onSelect(component)}
      >
        {childComponents.length > 0 && (
          <button
            className="p-1 hover:bg-accent/50 rounded"
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
          >
            {isExpanded ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </button>
        )}
        {!childComponents.length && <span className="w-6" />}
        <span className="truncate">
          {component.properties.name} ({component.type})
        </span>
      </div>
      {isExpanded && childComponents.length > 0 && (
        <div className="ml-4">
          {childComponents.map((child) => (
            <HierarchyItem
              key={child.id}
              component={child}
              components={components}
              onSelect={onSelect}
              selectedId={selectedId}
              level={level + 1}
              onDrop={onDrop}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface ComponentPanelProps {
  components?: CanvasComponent[];
  onSelect?: (component: CanvasComponent) => void;
  selectedId?: string | null;
  onDrop?: (draggedId: string, droppedOnId: string) => void;
}

export default function ComponentPanel({ 
  components = [], 
  onSelect = () => {}, 
  selectedId = null,
  onDrop,
}: ComponentPanelProps) {
  const rootComponents = components.filter(c => !c.properties.parentId);

  return (
    <Card className="w-64 p-4 m-4 flex flex-col">
      <Accordion type="multiple" defaultValue={["components", "hierarchy"]} className="space-y-4">
        <AccordionItem value="components" className="border-none">
          <AccordionTrigger className="py-0 hover:no-underline">
            <h2 className="font-semibold">Components</h2>
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-2 pt-4">
              {DRAGGABLE_COMPONENTS.map((component) => (
                <DraggableComponent key={component.type} {...component} />
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="hierarchy" className="border-none">
          <AccordionTrigger className="py-0 hover:no-underline">
            <h2 className="font-semibold">Hierarchy</h2>
          </AccordionTrigger>
          <AccordionContent>
            <ScrollArea className="h-[300px] mt-4">
              {rootComponents.map((component) => (
                <HierarchyItem
                  key={component.id}
                  component={component}
                  components={components}
                  onSelect={onSelect}
                  selectedId={selectedId}
                  onDrop={onDrop}
                />
              ))}
            </ScrollArea>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </Card>
  );
}